/* SKARVO "Catch the scarves" – game engine v4
   - catch scarves & beanies (+1), gold ones (+3), hearts (+1 life)
   - scissors cost a life, a BOMB blows up the basket = game over
   - TIER1 points -> reward 1 code, TIER2 points -> reward 2 code
   - players sign up first (email account or phone) and get a few games a day (reset at midnight, Tunisia time)
   Speed: every picture (basket, bomb, scissors, products) is drawn once into a small
   off-screen image and then only copied each frame. Nothing runs until the player presses Play. */
(() => {
  'use strict';

  /* ================= difficulty (tuned by simulation for ~10% wins) ================= */
  const P = {
    gap0: .41, gapDecay: .018, gapMin: .155,         // seconds between drops
    ramp: 15.5,                                      // seconds until full speed
    vy0: 265, vyRamp: 410, vyRand: 80, goldVy: 90,   // fall speed (px/s)
    pBomb0: .245, pBombRamp: .21,                    // bomb share
    pScis0: .165, pScisRamp: .08,                    // scissors share
    pGold: .035, pHeart: .018,
    bagFrac: .185, bagMin: 61, bagMax: 85,           // basket width
    follow: 16,                                      // how fast the basket follows the finger
    pad: -.15, bombPad: .57, scisPad: .12,           // hit-box: products must land inside, bomb/scissors touch easily
    bombSway: 80, bombSwayRamp: 230
  };

  /* ================= pure game logic (also used by the simulator) ================= */
  function newRound(W, H, lives, rand) {
    const w = Math.max(P.bagMin, Math.min(P.bagMax, W * P.bagFrac)), h = Math.round(w * .66);
    return { W, H, rand: rand || Math.random, t: 0, score: 0, lives, maxLives: lives, items: [], spawnT: .7,
      bag: { x: W / 2, tx: W / 2, w, h, top: H - h - 14 }, over: false, ev: [] };
  }
  function spawn(st, lv) {
    const R = st.rand, r = R();
    const pB = P.pBomb0 + lv * P.pBombRamp, pS = P.pScis0 + lv * P.pScisRamp, pH = st.lives < st.maxLives ? P.pHeart : 0;
    let type = 'item';
    if (r < pB) type = 'bomb';
    else if (r < pB + pS) type = 'scissors';
    else if (r < pB + pS + P.pGold) type = 'gold';
    else if (r < pB + pS + P.pGold + pH) type = 'heart';
    const s = type === 'bomb' ? 52 : type === 'scissors' ? 54 : type === 'heart' ? 40 : 58;
    st.items.push({ type, s, r: s * .4, x: s / 2 + R() * (st.W - s), y: -s, age: 0,
      vy: P.vy0 + lv * P.vyRamp + R() * P.vyRand + (type === 'gold' ? P.goldVy : 0),
      vx: type === 'bomb' ? (R() - .5) * 2 * (P.bombSway + lv * P.bombSwayRamp) : 0,
      sway: R() * 6.28, rot: R() * 6.28, vr: (R() - .5) * (type === 'scissors' ? 7 : 2.4), k: (R() * 1e6) | 0 });
  }
  function step(st, dt) {
    if (st.over) return;
    const b = st.bag, lv = Math.min(1, st.t / P.ramp);
    st.t += dt;
    b.x += (b.tx - b.x) * Math.min(1, dt * P.follow);
    st.spawnT -= dt;
    if (st.spawnT <= 0) { spawn(st, lv); st.spawnT = Math.max(P.gapMin, P.gap0 - st.t * P.gapDecay) * (.75 + st.rand() * .5); }
    const line = b.top + 8;
    for (let i = st.items.length - 1; i >= 0; i--) {
      const it = st.items[i], py = it.y;
      it.y += it.vy * dt; it.rot += it.vr * dt; it.age += dt;
      if (it.vx) {
        it.sway += dt * 3; it.x += (it.vx + Math.sin(it.sway) * 60) * dt;
        if (it.x < it.s / 2 || it.x > st.W - it.s / 2) { it.vx = -it.vx; it.x = Math.max(it.s / 2, Math.min(st.W - it.s / 2, it.x)); }
      }
      if (py + it.r < line && it.y + it.r >= line) {
        const pad = it.type === 'bomb' ? P.bombPad : it.type === 'scissors' ? P.scisPad : P.pad;
        if (Math.abs(it.x - b.x) < b.w / 2 + it.r * pad) {
          st.items.splice(i, 1);
          if (it.type === 'bomb') { st.over = true; st.lives = 0; st.ev.push({ e: 'bomb', it }); return; }
          if (it.type === 'scissors') { st.lives--; st.ev.push({ e: 'cut', it }); }
          else if (it.type === 'heart') { st.lives = Math.min(st.maxLives, st.lives + 1); st.ev.push({ e: 'heart', it }); }
          else { st.score += it.type === 'gold' ? 3 : 1; st.ev.push({ e: 'catch', it }); }
          if (st.lives <= 0) { st.over = true; st.ev.push({ e: 'dead' }); return; }
          continue;
        }
      }
      if (it.y - it.s > st.H) st.items.splice(i, 1);
    }
  }
  const core = { P, newRound, step };
  if (typeof globalThis !== 'undefined') globalThis.SKVGameCore = core;
  if (typeof document === 'undefined') return;

  /* ================= art: drawn once into sprites ================= */
  const SC = 2; // sprites are drawn at 2x so they stay sharp
  const mk = (w, h) => { const c = document.createElement('canvas'); c.width = Math.ceil(w * SC); c.height = Math.ceil(h * SC); const x = c.getContext('2d'); x.scale(SC, SC); return [c, x]; };
  const rr = (c, x, y, w, h, r) => { c.beginPath(); c.moveTo(x + r, y); c.arcTo(x + w, y, x + w, y + h, r); c.arcTo(x + w, y + h, x, y + h, r); c.arcTo(x, y + h, x, y, r); c.arcTo(x, y, x + w, y, r); c.closePath(); };
  const ART = {};

  ART.bomb = (() => { // glossy black bomb, red band, white skull, brass cap, fuse
    const S = 52, [cv, c] = mk(S + 24, S + 24), cx = (S + 24) / 2, cy = cx + 5, R = S * .4;
    c.fillStyle = 'rgba(0,0,0,.35)'; c.beginPath(); c.ellipse(cx + 3, cy + R * .95, R * .8, R * .22, 0, 0, 6.28); c.fill();
    let g = c.createRadialGradient(cx - R * .35, cy - R * .4, R * .1, cx, cy, R);
    g.addColorStop(0, '#5b5b62'); g.addColorStop(.35, '#1d1d22'); g.addColorStop(1, '#030304');
    c.fillStyle = g; c.beginPath(); c.arc(cx, cy, R, 0, 6.28); c.fill();
    c.save(); c.beginPath(); c.arc(cx, cy, R, 0, 6.28); c.clip();
    g = c.createLinearGradient(0, cy + R * .2, 0, cy + R * .48); g.addColorStop(0, '#e01b2c'); g.addColorStop(1, '#7d0a14');
    c.fillStyle = g; c.fillRect(cx - R, cy + R * .22, R * 2, R * .26);
    c.fillStyle = 'rgba(255,255,255,.18)'; c.fillRect(cx - R, cy + R * .22, R * 2, 1.5);
    c.restore();
    c.fillStyle = '#f4f1ec'; c.beginPath(); c.arc(cx, cy - R * .08, R * .3, 0, 6.28); c.fill();
    rr(c, cx - R * .17, cy + R * .1, R * .34, R * .14, 2); c.fill();
    c.fillStyle = '#0b0b0d'; c.beginPath(); c.arc(cx - R * .12, cy - R * .1, R * .085, 0, 6.28); c.arc(cx + R * .12, cy - R * .1, R * .085, 0, 6.28); c.fill();
    c.beginPath(); c.moveTo(cx, cy + R * .0); c.lineTo(cx - R * .04, cy + R * .07); c.lineTo(cx + R * .04, cy + R * .07); c.fill();
    c.strokeStyle = 'rgba(255,255,255,.55)'; c.lineWidth = 2.2; c.lineCap = 'round';
    c.beginPath(); c.arc(cx, cy, R * .8, Math.PI * 1.15, Math.PI * 1.42); c.stroke();
    c.fillStyle = 'rgba(255,255,255,.85)'; c.beginPath(); c.arc(cx - R * .5, cy - R * .5, 2.2, 0, 6.28); c.fill();
    g = c.createLinearGradient(cx - 7, 0, cx + 7, 0); g.addColorStop(0, '#6e5a2a'); g.addColorStop(.5, '#e6c46a'); g.addColorStop(1, '#6e5a2a');
    c.fillStyle = g; rr(c, cx - 7, cy - R - 6, 14, 9, 2); c.fill();
    c.strokeStyle = '#3a2b14'; c.lineWidth = 3; c.beginPath(); c.moveTo(cx, cy - R - 6); c.bezierCurveTo(cx + 2, cy - R - 16, cx + 12, cy - R - 12, cx + 15, cy - R - 18); c.stroke();
    c.strokeStyle = '#c9a25a'; c.lineWidth = 1.2; c.setLineDash([2, 2]); c.stroke(); c.setLineDash([]);
    return { cv, w: S + 24, h: S + 24, tip: [15, -R - 18 + 5] };
  })();
  ART.glow = (() => { const S = 110, [cv, c] = mk(S, S), g = c.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    g.addColorStop(0, 'rgba(255,40,40,.55)'); g.addColorStop(.45, 'rgba(255,40,40,.18)'); g.addColorStop(1, 'rgba(255,40,40,0)');
    c.fillStyle = g; c.fillRect(0, 0, S, S); return { cv, w: S, h: S }; })();
  ART.gold = (() => { const S = 110, [cv, c] = mk(S, S), g = c.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    g.addColorStop(0, 'rgba(255,200,60,.6)'); g.addColorStop(.5, 'rgba(255,190,40,.2)'); g.addColorStop(1, 'rgba(255,190,40,0)');
    c.fillStyle = g; c.fillRect(0, 0, S, S); return { cv, w: S, h: S }; })();

  ART.blade = (() => { // one half of the scissors, pivot in the middle of the sprite
    const W = 40, H = 84, [cv, c] = mk(W, H), cx = W / 2, cy = H / 2;
    let g = c.createLinearGradient(cx - 7, 0, cx + 7, 0); g.addColorStop(0, '#6f7782'); g.addColorStop(.45, '#ffffff'); g.addColorStop(.6, '#c9d0d8'); g.addColorStop(1, '#4e555e');
    c.fillStyle = g; c.beginPath(); c.moveTo(cx - 5.5, cy + 4); c.lineTo(cx + 5, cy + 4); c.lineTo(cx + 1.5, cy - 38); c.quadraticCurveTo(cx, cy - 41, cx - 1.4, cy - 37); c.closePath(); c.fill();
    c.strokeStyle = 'rgba(0,0,0,.35)'; c.lineWidth = .8; c.stroke();
    c.strokeStyle = 'rgba(255,255,255,.95)'; c.lineWidth = 1; c.beginPath(); c.moveTo(cx - 5, cy + 2); c.lineTo(cx - 1.2, cy - 35); c.stroke();
    c.fillStyle = '#3d4249'; rr(c, cx - 4.5, cy + 3, 9, 9, 2); c.fill();
    g = c.createLinearGradient(0, cy + 10, 0, cy + 40); g.addColorStop(0, '#ff4456'); g.addColorStop(1, '#8a0b16');
    c.strokeStyle = g; c.lineWidth = 6.5; c.beginPath(); c.ellipse(cx, cy + 25, 10, 13, 0, 0, 6.28); c.stroke();
    c.strokeStyle = 'rgba(255,255,255,.5)'; c.lineWidth = 1.6; c.beginPath(); c.ellipse(cx, cy + 25, 10, 13, 0, Math.PI * 1.1, Math.PI * 1.5); c.stroke();
    return { cv, w: W, h: H };
  })();
  ART.screw = (() => { const [cv, c] = mk(10, 10), g = c.createRadialGradient(4, 4, 0, 5, 5, 5);
    g.addColorStop(0, '#fff'); g.addColorStop(1, '#5d636b'); c.fillStyle = g; c.beginPath(); c.arc(5, 5, 3.4, 0, 6.28); c.fill(); return { cv, w: 10, h: 10 }; })();
  ART.heart = (() => { const S = 40, [cv, c] = mk(S + 8, S + 8); c.font = `${S}px serif`; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText('❤️', (S + 8) / 2, (S + 8) / 2 + 2); return { cv, w: S + 8, h: S + 8 }; })();

  function makeBasket(w, h) { // woven basket with a knitted SKARVO scarf over the front and a tag
    const pad = 22, [cv, c] = mk(w + pad * 2, h + pad * 2), x0 = pad, y0 = pad, rim = h * .2;
    c.lineCap = 'round';
    c.strokeStyle = '#6b4320'; c.lineWidth = 7; c.beginPath(); c.ellipse(x0 + w / 2, y0 + rim * .6, w * .34, h * .62, 0, Math.PI, 0); c.stroke();
    c.strokeStyle = '#c48a4a'; c.lineWidth = 3.5; c.stroke();
    c.strokeStyle = 'rgba(255,230,180,.5)'; c.lineWidth = 1; c.setLineDash([3, 4]); c.stroke(); c.setLineDash([]);
    const body = () => { c.beginPath(); c.moveTo(x0 + 2, y0 + rim * .5); c.lineTo(x0 + w - 2, y0 + rim * .5); c.lineTo(x0 + w - w * .1, y0 + h - 6); c.quadraticCurveTo(x0 + w - w * .11, y0 + h, x0 + w - w * .17, y0 + h); c.lineTo(x0 + w * .17, y0 + h); c.quadraticCurveTo(x0 + w * .11, y0 + h, x0 + w * .1, y0 + h - 6); c.closePath(); };
    c.fillStyle = 'rgba(0,0,0,.45)'; c.beginPath(); c.ellipse(x0 + w / 2, y0 + h + 4, w * .44, 5, 0, 0, 6.28); c.fill();
    body(); c.fillStyle = '#8a5a2b'; c.fill();
    c.save(); body(); c.clip();
    const rowH = 7, cols = ['#d39a57', '#b97c3c'];
    for (let r = 0, y = y0 + rim * .5; y < y0 + h; r++, y += rowH) for (let x = x0 - 12 + (r % 2) * 7; x < x0 + w + 12; x += 14) {
      c.fillStyle = cols[(r + ((x / 14) | 0)) % 2]; rr(c, x, y + 1, 12, rowH - 1.5, 3.2); c.fill();
      c.fillStyle = 'rgba(255,240,200,.35)'; c.fillRect(x + 2, y + 1.6, 8, 1.2);
    }
    c.fillStyle = 'rgba(70,40,15,.55)'; for (let x = x0 + 5; x < x0 + w; x += 14) c.fillRect(x, y0, 1.6, h);
    let g = c.createLinearGradient(x0, 0, x0 + w, 0); g.addColorStop(0, 'rgba(0,0,0,.45)'); g.addColorStop(.3, 'rgba(0,0,0,0)'); g.addColorStop(.7, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(0,0,0,.5)');
    c.fillStyle = g; c.fillRect(x0, y0, w, h);
    g = c.createLinearGradient(0, y0, 0, y0 + h); g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(0,0,0,.4)'); c.fillStyle = g; c.fillRect(x0, y0, w, h);
    c.restore();
    g = c.createLinearGradient(0, y0, 0, y0 + rim); g.addColorStop(0, '#e4ad68'); g.addColorStop(1, '#8f5a26');
    c.fillStyle = g; rr(c, x0 - 3, y0, w + 6, rim, rim / 2); c.fill();
    c.save(); rr(c, x0 - 3, y0, w + 6, rim, rim / 2); c.clip(); c.strokeStyle = 'rgba(80,45,15,.55)'; c.lineWidth = 1.6;
    for (let x = x0 - 8; x < x0 + w + 8; x += 5) { c.beginPath(); c.moveTo(x, y0 + rim); c.lineTo(x + rim * .8, y0); c.stroke(); } c.restore();
    // knitted scarf draped across the front
    const sy = y0 + rim + h * .1, band = h * .2;
    c.save(); c.beginPath(); c.moveTo(x0 + w * .06, sy); c.quadraticCurveTo(x0 + w * .5, sy + band * 1.3, x0 + w * .96, sy - band * .1);
    c.lineTo(x0 + w * .96, sy + band * .9); c.quadraticCurveTo(x0 + w * .5, sy + band * 2.3, x0 + w * .06, sy + band); c.closePath();
    g = c.createLinearGradient(0, sy, 0, sy + band * 2); g.addColorStop(0, '#d61f30'); g.addColorStop(1, '#8a0d18'); c.fillStyle = g; c.fill(); c.clip();
    c.fillStyle = '#f4f1ec'; for (let x = x0 + w * .22; x < x0 + w; x += w * .22) c.fillRect(x, y0, w * .05, h);
    c.strokeStyle = 'rgba(0,0,0,.18)'; c.lineWidth = 1; for (let x = x0; x < x0 + w; x += 3.5) { c.beginPath(); c.moveTo(x, sy - band); c.lineTo(x + 2, sy + band * 3); c.stroke(); }
    c.restore();
    c.strokeStyle = '#c4192a'; c.lineWidth = 1.5; for (let i = 0; i < 4; i++) { const fx = x0 + w * .86 + i * 2.6; c.beginPath(); c.moveTo(fx, sy + band * 1.1); c.lineTo(fx + .5, sy + band * 1.1 + 6 + (i % 2) * 2); c.stroke(); }
    // tag
    const tw = Math.max(46, w * .62), th = 15, tx = x0 + w / 2 - tw / 2, ty = y0 + h - th - h * .1;
    c.fillStyle = 'rgba(0,0,0,.35)'; rr(c, tx + 1, ty + 2, tw, th, 4); c.fill();
    c.fillStyle = '#111114'; rr(c, tx, ty, tw, th, 4); c.fill(); c.strokeStyle = 'rgba(255,255,255,.75)'; c.lineWidth = 1; rr(c, tx + 2, ty + 2, tw - 4, th - 4, 3); c.stroke();
    c.fillStyle = '#fff'; c.textAlign = 'center'; c.textBaseline = 'middle';
    let fs = Math.round(th * .66); c.font = `700 ${fs}px Oswald, Impact, 'Arial Narrow', sans-serif`;
    while (fs > 6 && c.measureText('SKARVO').width > tw - 10) { fs--; c.font = `700 ${fs}px Oswald, Impact, 'Arial Narrow', sans-serif`; }
    c.fillText('SKARVO', tx + tw / 2, ty + th / 2 + .5);
    return { cv, w: w + pad * 2, h: h + pad * 2, pad };
  }

  const PALETTE = [['#c4192a', '#f4f1ec'], ['#f4f1ec', '#111114'], ['#2e5a3a', '#f4f1ec'], ['#1f2f5c', '#f4f1ec'], ['#d98aa6', '#111114']];
  function makeCard(img, gold, pal) { // product photo on a rounded card (shadow baked in)
    const S = 58, pad = 10, [cv, c] = mk(S + pad * 2, S + pad * 2), x = pad, y = pad;
    c.shadowColor = gold ? 'rgba(255,190,40,.95)' : 'rgba(0,0,0,.55)'; c.shadowBlur = gold ? 14 : 8; c.shadowOffsetY = gold ? 0 : 3;
    if (gold) { const g = c.createLinearGradient(x, y, x + S, y + S); g.addColorStop(0, '#fff1b8'); g.addColorStop(.5, '#f2b632'); g.addColorStop(1, '#a8740e'); c.fillStyle = g; }
    else c.fillStyle = '#fff';
    rr(c, x, y, S, S, 12); c.fill(); c.shadowColor = 'transparent';
    c.save(); rr(c, x + 3.5, y + 3.5, S - 7, S - 7, 9); c.clip(); c.fillStyle = '#fff'; c.fillRect(x, y, S, S);
    if (img) { const iw = img.naturalWidth, ih = img.naturalHeight, k = Math.max((S - 7) / iw, (S - 7) / ih); c.drawImage(img, x + S / 2 - iw * k / 2, y + S / 2 - ih * k / 2, iw * k, ih * k); }
    else { const [a, b] = pal; c.translate(x + S / 2, y + S / 2); c.fillStyle = a; rr(c, -S * .16, -S * .4, S * .32, S * .74, 3); c.fill(); c.fillStyle = b; for (let i = 0; i < 4; i++) c.fillRect(-S * .16, -S * .3 + i * S * .17, S * .32, S * .05); }
    c.restore();
    if (gold) { c.fillStyle = '#111114'; c.beginPath(); c.arc(x + S - 6, y + 6, 11, 0, 6.28); c.fill(); c.fillStyle = '#ffd25a'; c.font = '700 11px Oswald, Impact, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText('+3', x + S - 6, y + 6.5); }
    return { cv, w: S + pad * 2, h: S + pad * 2 };
  }

  /* ================= page wiring ================= */
  document.querySelectorAll('[data-skv-game]:not([data-ready])').forEach(init);

  function init(root) {
    root.dataset.ready = '1';
    const d0 = root.dataset;
    const cfg = { tier1: +d0.tier1 || 25, tier2: +d0.tier2 || 50, code1: d0.code1 || '', code2: d0.code2 || '',
      reward1: d0.reward1 || 'Free delivery', reward2: d0.reward2 || '10% off', lives: +d0.lives || 3, limit: +d0.limit || 3,
      demo: d0.demo === 'true', shop: d0.shop || '/collections/all', admin: (d0.admin || '').trim().toLowerCase(), salt: d0.salt || '',
      customer: d0.customer || '', cphone: (d0.cphone || '').toLowerCase(), cname: d0.cname || '', cemail: d0.cemail || '' };
    const KEY = 'skv_game_v4', BEST = 'skv_game_best';
    const ls = { get(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } },
      set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }, del(k) { try { localStorage.removeItem(k); } catch (e) {} } };
    const $ = (s) => root.querySelector(s), $$ = (s) => root.querySelectorAll(s);
    const canvas = $('canvas'), ctx = canvas.getContext('2d', { alpha: true });
    const screens = {}; $$('[data-screen]').forEach(el => screens[el.dataset.screen] = el);
    const show = (k) => Object.entries(screens).forEach(([n, el]) => el.classList.toggle('is-on', n === k));

    // product photos: loaded only when the game comes near the screen
    let urls = []; try { urls = JSON.parse(($('[data-items]') || {}).textContent || '[]'); } catch (e) {}
    const cards = [], golds = []; let loaded = false;
    const loadImgs = () => { if (loaded) return; loaded = true;
      urls.forEach(u => { const i = new Image(); i.decoding = 'async';
        i.onload = () => { try { cards.push(makeCard(i, false)); golds.push(makeCard(i, true)); } catch (e) {} }; i.src = u; }); };
    if ('IntersectionObserver' in window) { const io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) { loadImgs(); io.disconnect(); } }, { rootMargin: '300px' }); io.observe(root); } else loadImgs();
    const fallback = PALETTE.map(p => makeCard(null, false, p)), fallbackGold = PALETTE.map(p => makeCard(null, true, p));
    const cardFor = (it) => { const gold = it.type === 'gold', list = gold ? golds : cards; return list.length ? list[it.k % list.length] : (gold ? fallbackGold : fallback)[it.k % PALETTE.length]; };

    let W = 0, H = 0, D = 1;
    const resize = () => { const r = canvas.getBoundingClientRect(); D = Math.min(devicePixelRatio || 1, 2);
      W = r.width; H = r.height; canvas.width = Math.round(W * D); canvas.height = Math.round(H * D); };
    addEventListener('resize', () => { if (!running) resize(); });

    let st = null, basket = null, running = false, looping = false, last = 0, count = 0, tierHit = false;
    let parts = [], pops = [], shake = 0, flash = 0, wob = 0, boomAt = 0;
    const hud = () => {
      const s = st ? st.score : 0, l = st ? Math.max(0, st.lives) : cfg.lives;
      $('[data-lives-el]').innerHTML = Array.from({ length: cfg.lives }, (_, i) => `<i class="${i < l ? 'on' : ''}"></i>`).join('');
      $('[data-score]').textContent = s;
      $('[data-bar]').style.width = Math.min(100, s / cfg.tier2 * 100) + '%';
      root.style.setProperty('--tier1', (cfg.tier1 / cfg.tier2 * 100) + '%');
      $('[data-next]').textContent = s < cfg.tier1 ? `${cfg.tier1 - s} to ${cfg.reward1}` : s < cfg.tier2 ? `${cfg.tier2 - s} to ${cfg.reward2}` : 'MAX!';
      root.classList.toggle('t1', s >= cfg.tier1);
    };

    // ----- start / gate -----
    // Players sign up on the game picker (Shopify account by email, or phone number).
    // Each player gets LIMIT games a day; the count resets at midnight Tunisia time.
    // The admin number plays without limit. A play-again code from the order email gives one extra game.
    const getS = () => { const s = ls.get(KEY) || {}; s.days = s.days || {}; s.used = s.used || []; return s; };
    const saveS = (s) => ls.set(KEY, s);
    const sha = async (t) => { const b = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(t));
      return Array.from(new Uint8Array(b), x => x.toString(16).padStart(2, '0')).join(''); };
    const tunisNow = () => { try { return new Date(new Date().toLocaleString('en-US', { timeZone: 'Africa/Tunis' })); } catch (e) { return new Date(); } };
    const today = () => { const t = tunisNow(); return t.getFullYear() + '-' + String(t.getMonth() + 1).padStart(2, '0') + '-' + String(t.getDate()).padStart(2, '0'); };
    const player = () => {
      if (typeof window.SKVPlayer === 'function') { const p = window.SKVPlayer(); if (p) return p; }
      if (cfg.customer) return { id: 'c' + cfg.customer, kind: 'account' };
      const p = ls.get('skv_player'); return p && p.phone ? { id: p.phone, kind: 'phone', phone: p.phone } : null;
    };
    const playedToday = (s, p) => { const r = s.days[p.id]; return r && r.d === today() ? r.n : 0; };
    const left = () => { const p = player(); if (!p) return cfg.limit; if (admin) return Infinity; return Math.max(0, cfg.limit - playedToday(getS(), p)); };
    const countPlay = () => { const s = getS(), p = player(); if (!p) return; const d = today(), r = s.days[p.id];
      s.days[p.id] = { d, n: r && r.d === d ? r.n + 1 : 1 };
      Object.keys(s.days).forEach(k => { if (s.days[k].d !== d) delete s.days[k]; }); // keep only today
      saveS(s); };
    const showLeft = () => { const n = left();
      $$('[data-left]').forEach(el => { el.textContent = !player() ? '' : n === Infinity ? 'Admin: unlimited games' : n > 0 ? `${n} game${n === 1 ? '' : 's'} left today` : 'No games left today'; }); };
    let admin = false;
    const checkAdmin = async () => { admin = false; if (!cfg.admin) return false; const p = player();
      if (p && p.phone) admin = (await sha(p.phone.replace('+216', ''))) === cfg.admin;
      if (!admin && cfg.cphone) admin = cfg.cphone === cfg.admin;
      return admin; };
    const urlReplay = (() => { try { return new URLSearchParams(location.search).get('replay') || ''; } catch (e) { return ''; } })();
    const checkReplay = async (txt) => { const m = String(txt).match(/(\d+)\D*([0-9a-f]{6})/i); if (!m || !cfg.salt) return null;
      const h = (await sha(m[1] + cfg.salt)).slice(0, 6); return h === m[2].toLowerCase() ? m[1] : null; };
    let resetT = 0;
    const tickReset = () => { const el = $('[data-reset-in]'); if (!el) return; const t = tunisNow(), m = new Date(t); m.setHours(24, 0, 0, 0);
      const s = Math.max(0, Math.round((m - t) / 1000)), h = Math.floor(s / 3600), mi = Math.floor(s % 3600 / 60);
      el.textContent = h > 0 ? `${h}h ${String(mi).padStart(2, '0')}min` : `${mi} min`;
      if (s <= 1) { clearInterval(resetT); showLeft(); show('start'); } };
    const renderAlready = () => { const s = getS(), l = s.last || {}, won = !!l.code;
      $('[data-already-win]').hidden = !won;
      if (won) { $('[data-already-reward]').textContent = l.reward; setCode($('[data-already] [data-code-box]'), l.code); }
      const sl = $('[data-shop-link]'); if (sl) sl.href = cfg.shop;
      if (urlReplay && !$('#skv-replay').value) $('#skv-replay').value = urlReplay;
      clearInterval(resetT); tickReset(); resetT = setInterval(tickReset, 30000);
      show('already'); };
    const begin = async () => { loadImgs();
      if (!player()) { document.dispatchEvent(new CustomEvent('skv:signup', { detail: 'catch' })); return; }
      await checkAdmin();
      if (admin) { start(); toast('Admin: unlimited games'); return; }
      if (left() <= 0) { if (urlReplay && await useReplay(urlReplay)) return; renderAlready(); return; }
      countPlay(); start(); };
    $$('[data-start]').forEach(b => b.addEventListener('click', begin));
    const useReplay = async (txt, errEl) => { const s = getS(), num = await checkReplay(txt);
      if (!num) { if (errEl) errEl.textContent = 'This code is not valid. Check your order email.'; return false; }
      if (s.used.includes(num)) { if (errEl) errEl.textContent = 'This code was already used.'; return false; }
      s.used = [...s.used, num]; saveS(s); start(); return true; };
    const replayForm = $('[data-replay-form]');
    if (replayForm) replayForm.addEventListener('submit', (e) => { e.preventDefault();
      const er = $('[data-replay-err]'); er.textContent = ''; useReplay($('#skv-replay').value, er); });

    function start() {
      const stg = $('.skv-game__stage'), rb = stg.getBoundingClientRect();
      if (rb.top < 0 || rb.bottom > innerHeight) stg.scrollIntoView({ behavior: 'smooth', block: 'center' });
      resize(); st = newRound(W, H, cfg.lives); basket = makeBasket(st.bag.w, st.bag.h);
      parts = []; pops = []; shake = flash = wob = 0; tierHit = false; count = 2.4; boomAt = 0;
      root.classList.add('playing'); root.classList.toggle('is-admin', admin); hud(); show(null); running = true; kick();
    }

    // ----- controls -----
    const setX = (cx) => { if (!st) return; const r = canvas.getBoundingClientRect(), b = st.bag; b.tx = Math.max(b.w / 2, Math.min(W - b.w / 2, cx - r.left)); };
    canvas.addEventListener('pointerdown', e => { setX(e.clientX); try { canvas.setPointerCapture(e.pointerId); } catch (_) {} });
    canvas.addEventListener('pointermove', e => { if (e.pointerType === 'mouse' || e.buttons) setX(e.clientX); });
    let kd = 0;
    addEventListener('keydown', e => { if (!running) return; if (e.key === 'ArrowLeft') kd = -1; if (e.key === 'ArrowRight') kd = 1; if (e.key.startsWith('Arrow')) e.preventDefault(); });
    addEventListener('keyup', e => { if ((e.key === 'ArrowLeft' && kd < 0) || (e.key === 'ArrowRight' && kd > 0)) kd = 0; });

    // ----- effects -----
    const burst = (x, y, cols, n, pw = 1) => { for (let i = 0; i < n && parts.length < 220; i++) { const a = Math.random() * 6.28, v = (80 + Math.random() * 220) * pw;
      parts.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 140 * pw, life: .8, max: .8, c: cols[i % cols.length], r: Math.random() * 6.28, w: 6 + Math.random() * 8, h: 3 + Math.random() * 3 }); } };
    const pop = (x, y, txt, c, big) => pops.push({ x, y, txt, c, life: .9, big });
    let toastT; const toast = (m) => { const el = $('[data-toast]'); el.textContent = m; el.classList.add('is-on'); clearTimeout(toastT); toastT = setTimeout(() => el.classList.remove('is-on'), 2600); };

    function handle(ev) {
      const b = st.bag, y = b.top;
      for (const { e, it } of ev) {
        if (e === 'catch') { const g = it.type === 'gold'; wob = .5; burst(it.x, y, g ? ['#ffd25a', '#fff1b8', '#c4192a'] : ['#c4192a', '#f4f1ec', '#ffffff'], g ? 22 : 12); pop(it.x, y - 12, g ? '+3' : '+1', g ? '#ffd25a' : '#fff', g); }
        else if (e === 'cut') { shake = .35; flash = .3; burst(it.x, y, ['#dfe4ea', '#8a929c', '#c4192a'], 14); pop(it.x, y - 12, 'SNIP!', '#ff4757'); if (navigator.vibrate) navigator.vibrate(80); }
        else if (e === 'heart') { burst(it.x, y, ['#ff3b6b', '#ff9ab5'], 14); pop(it.x, y - 12, '+❤', '#ff6b8b'); }
        else if (e === 'bomb') explode();
        else if (e === 'dead') { running = false; setTimeout(finish, 650); }
      }
      ev.length = 0;
      if (!tierHit && st.score >= cfg.tier1) { tierHit = true; toast(`🎁 ${cfg.reward1} unlocked! Keep going for ${cfg.reward2}`); }
      hud();
      if (running && st.score >= cfg.tier2) { running = false; st.over = true; setTimeout(finish, 400); }
    }
    function explode() {
      const b = st.bag, y = b.top + b.h / 2;
      burst(b.x, y, ['#ff4d00', '#ffb000', '#fff3c4', '#c4192a', '#d39a57', '#111'], 90, 1.9);
      pop(b.x, y - 34, 'BOOM!', '#ffb000', true);
      b.dead = true; boomAt = performance.now(); shake = .8; flash = .6; if (navigator.vibrate) navigator.vibrate([120, 60, 220]);
      running = false; st.items = []; hud(); setTimeout(finish, 1000);
    }

    // ----- loop -----
    function kick() { if (!looping) { looping = true; last = performance.now(); requestAnimationFrame(loop); } }
    function loop(now) {
      const busy = running || parts.length || pops.length || boomAt && now - boomAt < 700;
      if (!busy) { looping = false; draw(now); return; }
      const dt = Math.min(.033, (now - last) / 1000); last = now;
      if (st) {
        const b = st.bag;
        if (kd) b.tx = Math.max(b.w / 2, Math.min(W - b.w / 2, b.tx + kd * 620 * dt));
        if (running) { if (count > 0) { count -= dt; b.x += (b.tx - b.x) * Math.min(1, dt * P.follow); } else { step(st, dt); if (st.ev.length) handle(st.ev); } }
      }
      for (let i = parts.length - 1; i >= 0; i--) { const p = parts[i]; p.life -= dt; p.vy += 560 * dt; p.x += p.vx * dt; p.y += p.vy * dt; p.r += dt * 9; if (p.life <= 0) parts.splice(i, 1); }
      for (let i = pops.length - 1; i >= 0; i--) { const p = pops[i]; p.life -= dt; p.y -= 60 * dt; if (p.life <= 0) pops.splice(i, 1); }
      shake = Math.max(0, shake - dt); flash = Math.max(0, flash - dt); wob = Math.max(0, wob - dt * 3);
      draw(now);
      requestAnimationFrame(loop);
    }
    const spr = (s, x, y, k = 1) => ctx.drawImage(s.cv, x - s.w * k / 2, y - s.h * k / 2, s.w * k, s.h * k);
    function draw(now) {
      const ox = shake ? (Math.random() - .5) * 18 * shake : 0, oy = shake ? (Math.random() - .5) * 18 * shake : 0;
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(D, 0, 0, D, ox * D, oy * D);
      if (!st) return;
      const tt = now / 1000;
      for (const it of st.items) {
        if (it.type === 'bomb') {
          ctx.globalAlpha = .55 + Math.sin(tt * 9 + it.k) * .3; spr(ART.glow, it.x, it.y + 4, .9); ctx.globalAlpha = 1;
          const a = Math.sin(it.rot) * .25, c = Math.cos(a), s = Math.sin(a);
          ctx.setTransform(D * c, D * s, -D * s, D * c, (it.x + ox) * D, (it.y + oy) * D);
          ctx.drawImage(ART.bomb.cv, -ART.bomb.w / 2, -ART.bomb.h / 2, ART.bomb.w, ART.bomb.h);
          const [fx, fy] = ART.bomb.tip, fl = 2.5 + Math.random() * 2.5;
          ctx.fillStyle = '#ffec8a'; ctx.beginPath(); ctx.arc(fx, fy, fl, 0, 6.28); ctx.fill();
          ctx.fillStyle = Math.random() < .5 ? '#ff7a00' : '#ffb000';
          for (let i = 0; i < 3; i++) { ctx.fillRect(fx + (Math.random() - .5) * 12, fy - Math.random() * 10, 2, 2); }
          ctx.setTransform(D, 0, 0, D, ox * D, oy * D);
        } else if (it.type === 'scissors') {
          const open = .34 + Math.sin(tt * 16 + it.k) * .16, bl = ART.blade;
          ctx.translate(it.x, it.y); ctx.rotate(it.rot);
          ctx.rotate(open); ctx.drawImage(bl.cv, -bl.w / 2, -bl.h / 2, bl.w, bl.h);
          ctx.rotate(-open * 2); ctx.scale(-1, 1); ctx.drawImage(bl.cv, -bl.w / 2, -bl.h / 2, bl.w, bl.h);
          ctx.setTransform(D, 0, 0, D, ox * D, oy * D); spr(ART.screw, it.x, it.y);
        } else if (it.type === 'heart') { spr(ART.heart, it.x, it.y, 1 + Math.sin(tt * 7) * .08); }
        else {
          if (it.type === 'gold') { ctx.globalAlpha = .6 + Math.sin(tt * 6 + it.k) * .3; spr(ART.gold, it.x, it.y); ctx.globalAlpha = 1; }
          const a = Math.sin(it.rot) * .3, c = Math.cos(a), s = Math.sin(a), cd = cardFor(it);
          ctx.setTransform(D * c, D * s, -D * s, D * c, (it.x + ox) * D, (it.y + oy) * D);
          ctx.drawImage(cd.cv, -cd.w / 2, -cd.h / 2, cd.w, cd.h);
          ctx.setTransform(D, 0, 0, D, ox * D, oy * D);
        }
      }
      for (const p of parts) { const c = Math.cos(p.r), s = Math.sin(p.r);
        ctx.globalAlpha = Math.max(0, p.life / p.max); ctx.fillStyle = p.c;
        ctx.setTransform(D * c, D * s, -D * s, D * c, (p.x + ox) * D, (p.y + oy) * D); ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h); }
      ctx.globalAlpha = 1; ctx.setTransform(D, 0, 0, D, ox * D, oy * D);
      const b = st.bag;
      if (!b.dead && basket) {
        const sq = 1 + Math.sin(wob * 9) * wob * .07, lean = Math.max(-.12, Math.min(.12, (b.tx - b.x) * .003));
        const bx = b.x, by = b.top + b.h;
        ctx.translate(bx, by); ctx.rotate(lean); ctx.scale(1 / sq, sq);
        ctx.drawImage(basket.cv, -basket.w / 2, -basket.h + basket.pad, basket.w, basket.h);
        ctx.setTransform(D, 0, 0, D, ox * D, oy * D);
      }
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      for (const p of pops) { ctx.globalAlpha = Math.max(0, p.life / .9); ctx.fillStyle = p.c; ctx.font = `700 ${p.big ? 34 : 22}px Oswald, Impact, sans-serif`;
        ctx.strokeStyle = 'rgba(0,0,0,.6)'; ctx.lineWidth = 3; ctx.strokeText(p.txt, p.x, p.y); ctx.fillText(p.txt, p.x, p.y); }
      ctx.globalAlpha = 1;
      if (running && count > 0) { const n = Math.ceil(count / .8), f = (count % .8) / .8;
        ctx.globalAlpha = .4 + f * .6; ctx.fillStyle = '#fff'; ctx.font = `700 ${70 + (1 - f) * 30}px Oswald, Impact, sans-serif`; ctx.fillText(n, W / 2, H * .42); ctx.globalAlpha = 1; }
      if (flash) { ctx.fillStyle = `rgba(214,31,48,${flash * .55})`; ctx.fillRect(-20, -20, W + 40, H + 40); }
    }

    const sendToOrder = (s) => { if (cfg.demo) return;
      const l = s.last || {}, p = player() || {};
      fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { 'Game name': s.name || '', 'Game phone': p.phone || '', 'Game email': p.kind === 'account' ? cfg.cemail : '', 'Game score': String(l.score || 0), 'Game reward': l.reward || 'none' } }) }).catch(() => {}); };
    function finish() {
      running = false; root.classList.remove('playing');
      const score = st.score, best = Math.max(score, +ls.get(BEST) || 0); ls.set(BEST, best);
      let code = '', reward = '';
      if (score >= cfg.tier2) { code = cfg.code2; reward = cfg.reward2; }
      else if (score >= cfg.tier1) { code = cfg.code1; reward = cfg.reward1; }
      const s = getS();
      if (!admin) { const prev = s.last || {}; s.last = code || !prev.code ? { score, code, reward, at: Date.now() } : prev; saveS(s); sendToOrder(s); }
      if (!s.name && cfg.cname) s.name = cfg.cname;
      $$('[data-name-form]').forEach(f => f.hidden = false);
      $$('[data-name-form] input').forEach(i => { if (s.name && !i.value) i.value = s.name; });
      $$('[data-name-form] button').forEach(b => { b.disabled = false; b.textContent = b.closest('[data-required]') ? 'Get my code' : 'Save score'; });
      showLeft();
      const again = $('[data-again]'); if (again) again.textContent = left() > 0 ? 'Play again' : 'No games left today';
      if (!code) { $('[data-final]').textContent = score; $('[data-final-need]').textContent = cfg.tier1; const bb = $('[data-best]'); if (bb) bb.textContent = best; show('lose'); return; }
      $('[data-win-reward]').textContent = reward; $('[data-win-score]').textContent = score;
      setCode($('[data-win] [data-code-box]'), code); $('[data-reveal]').hidden = true; show('win');
      for (let i = 0; i < 6; i++) setTimeout(() => { burst(Math.random() * W, Math.random() * H * .5, ['#c4192a', '#ffd25a', '#fff', '#f4f1ec'], 26, 1.2); kick(); }, i * 220);
    }
    $$('[data-name-form]').forEach(f => f.addEventListener('submit', (e) => { e.preventDefault();
      const name = f.querySelector('input').value.trim().slice(0, 40), btn = f.querySelector('button');
      if (!name) { f.querySelector('input').focus(); return; }
      const s = getS(); s.name = name; saveS(s); if (!admin) sendToOrder(s);
      btn.disabled = true; btn.textContent = 'Saved ✓';
      if (f.hasAttribute('data-required')) { f.hidden = true; $('[data-reveal]').hidden = false; } }));
    function setCode(box, code) {
      box.querySelector('[data-code-text]').textContent = code || '—';
      const apply = box.parentElement.querySelector('[data-apply]');
      if (apply) apply.href = cfg.demo ? '#' : `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(cfg.shop)}`;
      if (apply && cfg.demo) apply.onclick = (e) => { e.preventDefault(); apply.textContent = 'On the real site this applies the code ✓'; };
    }
    root.addEventListener('click', async (e) => { const b = e.target.closest('[data-code-box]'); if (!b) return;
      try { await navigator.clipboard.writeText(b.querySelector('[data-code-text]').textContent); b.querySelector('small').textContent = 'Copied ✓'; } catch (_) {} });
    if (cfg.demo) root._skv = () => st; // test page only
    checkAdmin().then(showLeft); showLeft();
    hud();
  }
})();
